import clsx from 'clsx'
import { forwardRef, useCallback, type ComponentPropsWithoutRef } from 'react'

import {
  NormalizedInputNumber,
  type NormalizedInputNumberHandle
} from '@/components/elements'

type Props = Omit<
  ComponentPropsWithoutRef<typeof NormalizedInputNumber>,
  'allowDecimal' | 'allowNegative' | 'onChange' | 'value'
> & {
  value?: number | null
  onChange?: (value: number | null) => void
}

/**
 * 出荷情報の重量入力用コンポーネント
 * - kg単位での入力、小数点第一位まで（整数値は「.0」を表示しない）
 * - NormalizedInputNumber を利用し、無効値は表示保持＆onChangeしない
 */
export const ShipmentOrderWeightInput = forwardRef<
  NormalizedInputNumberHandle,
  Props
>(({ className, ...props }, ref) => {
  // 小数点第一位までに丸める（VOC_PJ-949 #4435）
  const handleChangeWeight = useCallback(
    (value: number | null, onChange?: (v: number | null) => void) => {
      if (!onChange) return
      const formatted = value === null ? null : Math.round(value * 10) / 10
      onChange(formatted)
    },
    []
  )

  const formatDisplay = useCallback((v: number | null) => {
    if (v == null) return ''
    const rounded = Math.round(v * 10) / 10
    // minimumFractionDigits を指定しないため、整数値は「.0」を出さずそのまま表示される
    return new Intl.NumberFormat('en-US', {
      useGrouping: false,
      maximumFractionDigits: 1
    }).format(rounded)
  }, [])

  return (
    <NormalizedInputNumber
      {...props}
      ref={ref}
      className={clsx('w-full text-right', className)}
      placeholder="0"
      onChange={(v) => handleChangeWeight(v, props.onChange)}
      formatText={formatDisplay}
    />
  )
})
